// Widgets.js
import React, { useEffect, useState, useMemo, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { __ } from '@wordpress/i18n';
import iziToast from 'izitoast';

const THHA_URL = window.thhaAdminSettings.pluginUrl;

// Helper component for the icon (using a simple placeholder)
const GridIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
    </svg>
);
const SearchIcon = ({ className }) => (
    <svg width="21" height="21" className={className} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M14.875 14.875L18.375 18.375" stroke="#98989A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
<path d="M16.625 9.625C16.625 5.759 13.491 2.625 9.625 2.625C5.759 2.625 2.625 5.759 2.625 9.625C2.625 13.491 5.759 16.625 9.625 16.625C13.491 16.625 16.625 13.491 16.625 9.625Z" stroke="#98989A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>

);
const CheckIcon = ({ className }) => (
    <svg width="18" height="18" className={className} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.25 1.84335C10.5397 1.62026 9.7839 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 8.2161 16.3797 7.46028 16.1567 6.75" stroke="#98989A" strokeWidth="1.5" strokeLinecap="round"/>
<path d="M6.375 7.125L9 9.75L15.7502 2.25" stroke="#98989A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>);

const iconHtml = ReactDOMServer.renderToStaticMarkup(
        <CheckIcon className="checkmark-circle" />
    );

// Component for a single category tab
const CategoryTab = ({ name, count, active, onClick }) => {
    return (
        <div 
            className={`category-tab ${active ? 'active' : ''}`}
            onClick={() => onClick(name)} 
        >
            <span className="icon-wrapper">
                <GridIcon />
            </span>
            {/* Capitalize and display the name */}
            <span className="tab-name">{name.charAt(0).toUpperCase() + name.slice(1)}</span>
            <span className="count">{count}</span>
        </div>
    );
};

const Widgets = ({ 
    numberOfCategories, 
    perCategoryItem, 
    isSearchEnable = true, 
    setActiveItem,
    widgetsData,
    enabledWidgets,
    setEnabledWidgets,
    activeCategory,    
    setActiveCategory,
    loading
 }) => {
    
    const [isSaving, setIsSaving] = useState(false);
    
    // State for Search Query
    const [searchQuery, setSearchQuery] = useState('');

    // State for Widget Filter (all, free, pro) and Dropdown Menu
    const [widgetFilter, setWidgetFilter] = useState('all');
    const [menuOpen, setMenuOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const widgetsByClass = useMemo(() => {
        const map = {};
        Object.values(widgetsData.all || {}).forEach((category) => {
            Object.entries(category || {}).forEach(([widgetClass, widgetData]) => {
                map[widgetClass] = widgetData;
            });
        });
        return map;
    }, [widgetsData.all]);

    const showDependencyLockedNotice = (dependencyMessage) => {
        iziToast.warning({
            id: 'dependency-locked-toast',
            title: __('Dependency Required', 'huge-addons-for-elementor'),
            message: dependencyMessage || __('This widget requires an active dependency plugin.', 'huge-addons-for-elementor'),
            position: 'topRight',
            timeout: 3500,
        });
    };

    

    // 1. Generate Categories dynamically
    const categories = useMemo(() => {
        if (!widgetsData || !widgetsData.all) return [];
    
        let allCategories = Object.keys(widgetsData.all)
            .filter(key => key !== 'all') 
            .sort()
            .map(key => {
                const widgets = Object.values(widgetsData.all[key] || {});
                const count = widgets.filter(w => {
                    if (widgetFilter === 'free') return !w.is_pro;
                    if (widgetFilter === 'pro') return w.is_pro;
                    return true;
                }).length;
                return {
                    name: key,
                    count: count
                };
            });

        if (numberOfCategories !== 'all' && typeof numberOfCategories === 'number') {
            allCategories = allCategories.slice(0, numberOfCategories);
        }
    
        const allWidgetsList = Object.values(widgetsData.all).flatMap(category => Object.values(category));
        const filteredAllWidgetsList = allWidgetsList.filter(w => {
            if (widgetFilter === 'free') return !w.is_pro;
            if (widgetFilter === 'pro') return w.is_pro;
            return true;
        });
        return [{ name: 'all', count: filteredAllWidgetsList.length }, ...allCategories];
    }, [widgetsData, numberOfCategories, widgetFilter]);

    // 2. Filter Widgets based on the active category AND search query
    const filteredWidgets = useMemo(() => {
        const allWidgets = widgetsData.all || {};
        if (Object.keys(allWidgets).length === 0) return {};

        const lowerSearchQuery = searchQuery.toLowerCase();
        
        let widgetsToRender = {};

        // Get the list of category keys that should be visible based on the prop
        const visibleCategoryKeys = categories.slice(1).map(c => c.name); // Exclude 'all'

        // Determine which categories to process for rendering
        let categoriesToProcess = [];
        if (activeCategory === 'all') {
            categoriesToProcess = visibleCategoryKeys;
        } else if (visibleCategoryKeys.includes(activeCategory) || numberOfCategories === 'all') {
            categoriesToProcess = [activeCategory];
        }
            
        for (const categoryKey of categoriesToProcess) {
            const categoryWidgets = allWidgets[categoryKey] || {};
            let filteredCategory = {};

            for (const [widgetClass, widgetData] of Object.entries(categoryWidgets)) {
                // Filter by Free vs Pro status
                if (widgetFilter === 'free' && widgetData.is_pro) continue;
                if (widgetFilter === 'pro' && !widgetData.is_pro) continue;

                if (widgetData.name.toLowerCase().includes(lowerSearchQuery)) {
                    filteredCategory[widgetClass] = widgetData;
                }
            }
            
            if (Object.keys(filteredCategory).length > 0) {
                widgetsToRender[categoryKey] = filteredCategory;
            }
        }

        return widgetsToRender;
    }, [widgetsData.all, activeCategory, searchQuery, widgetFilter, categories]);
    

    // Function to handle checkbox changes
    const handleToggle = (widgetClass, isChecked) => {
        const widgetData = widgetsByClass[widgetClass] || {};
        if (widgetData.dependency_locked) {
            showDependencyLockedNotice(widgetData.dependency_message);
            return;
        }

        let newEnabled;
        if (isChecked) {
            // Add widgetClass to enabledWidgets if checked
            newEnabled = [...enabledWidgets, widgetClass];
        } else {
            // Remove widgetClass from enabledWidgets if unchecked
            newEnabled = enabledWidgets.filter(item => item !== widgetClass);
        }
        setEnabledWidgets(newEnabled);
        handleSaveSettingsAction(newEnabled);
    };
    
    // Function to handle Enable All / Disable All
    const handleBulkAction = (action) => {
        const isProActive = !!window.thhaAdminSettings.is_pro_active;
        const allWidgetClasses = Object.values(widgetsData.all)
            .flatMap(category => Object.entries(category))
            .filter(([, widgetData]) => (!widgetData.is_pro || isProActive) && widgetData.status === 'available' && !widgetData.dependency_locked)
            .map(([widgetClass]) => widgetClass);
        
        let newEnabled;
        if (action === 'enable') {
            newEnabled = Array.from(new Set(allWidgetClasses));
        } else if (action === 'disable') {
            newEnabled = [];
        }
        setEnabledWidgets(newEnabled);
        handleSaveSettingsAction(newEnabled);
    };

    const handleLockedToggleClick = (event, dependencyMessage) => {
        event.preventDefault();
        event.stopPropagation();
        showDependencyLockedNotice(dependencyMessage);
    };

    // Function to handle saving changes via AJAX
    const handleSaveChanges = (e) => {
        e.preventDefault();
        handleSaveSettingsAction(enabledWidgets);
    };

    const handleSaveSettingsAction = (widgetsToSave) => {
        setIsSaving(true);
        const formData = new FormData();
        formData.append('action', 'thha_save_addons_ajax');
        formData.append('_wpnonce', window.thhaAdminSettings.saveNonce);
        widgetsToSave.forEach(widgetClass => {
            formData.append('enabled_widgets[]', widgetClass);
        });
        
        fetch(window.thhaAdminSettings.ajaxurl, {
            method: 'POST',
            body: formData,
        })
        .then(res => {
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
        })
        .then(response => {
            if (response.success) {
                if (response.data && Array.isArray(response.data.enabled_widgets)) {
                    setEnabledWidgets(response.data.enabled_widgets);
                }
                iziToast.show({
                    id: 'success-toast',
                    message: 'Successfully saved!',
                    position: 'topRight',
                    backgroundColor: '#FFFFFF',
                    messageColor: '#19192C',
                    messageSize: '16',
                    messageLineHeight: '24',
                    iconUrl: `data:image/svg+xml;utf8,${encodeURIComponent(iconHtml)}`,
                    displayMode: 0,
                    close: true,
                    closeColor: '#98989A',
                    imageWidth: 50,
                    maxWidth: 400,
                    layout: 2,
                    transitionIn: 'fadeInDown',
                    theme: 'light', 
                    progressBarColor: '#22C55E',
                    boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
                });
            } else {
                throw new Error(response.data || 'Unknown server error');
            }
            
        })
        .catch((error) => {
            console.error('Error saving widgets:', error);
            iziToast.error({
                title: 'ERROR',
                message: `Widget updating failed: ${error.message}`,
                position: 'topCenter',
                timeout: 5000
            });
        })
        .finally(() => {
            setIsSaving(false);
        });
    };


    // Redirect if a category is active but not in the list of rendered categories
    useEffect(() => {
        const isDashboardView = numberOfCategories !== 'all';
        const activeCategoryExists = categories.some(c => c.name === activeCategory);
        if (isDashboardView && !activeCategoryExists && activeCategory !== 'all') {
            window.location.href = `${window.location.pathname}?page=thha-addons-manager&tab=widgets`;
        }
    }, [activeCategory, categories, numberOfCategories]);

    if (loading) return <div className="widgets-section loading">{__('Loading Widgets...', 'huge-addons-for-elementor')}</div>;

    return (
        <div className="widgets-section">
            <div className="section-header">
                <div className="header-left">
                    <h1 className="title">{__('Widget', 'huge-addons-for-elementor')}</h1>
                    {isSearchEnable && (
                        <div className="search-container">
                            <SearchIcon className="search-icon" /> 
                            <input
                                type="text"
                                className="search-field"
                                placeholder={__('Search Widget', 'huge-addons-for-elementor')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    )}
                </div>

                <div className="header-right">
                    {isSearchEnable ? (
                        <div className="actions">
                            <button 
                                type="button" 
                                className="disable-btn"
                                onClick={() => handleBulkAction('disable')}
                            >
                                {__('Disable', 'huge-addons-for-elementor')}
                            </button>
                            <button 
                                type="button" 
                                className="enable-all-btn"
                                onClick={() => handleBulkAction('enable')}
                            >
                                {__('Enable All', 'huge-addons-for-elementor')}
                            </button>
                            <div className="menu-dropdown-container" ref={dropdownRef}>
                                <button 
                                    type="button" 
                                    className={`menu-btn ${menuOpen ? 'active' : ''}`}
                                    onClick={() => setMenuOpen(!menuOpen)}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="3" y1="12" x2="21" y2="12"></line>
                                        <line x1="3" y1="6" x2="21" y2="6"></line>
                                        <line x1="3" y1="18" x2="21" y2="18"></line>
                                    </svg>
                                </button>
                                {menuOpen && (
                                    <div className="menu-dropdown">
                                        <div className="dropdown-label">{__('Filter Widgets', 'huge-addons-for-elementor')}</div>
                                        <button 
                                            type="button"
                                            className={`dropdown-item ${widgetFilter === 'free' ? 'active' : ''}`}
                                            onClick={() => {
                                                setWidgetFilter(widgetFilter === 'free' ? 'all' : 'free');
                                                setMenuOpen(false);
                                            }}
                                        >
                                            <span>{__('Free', 'huge-addons-for-elementor')}</span>
                                            {widgetFilter === 'free' && (
                                                <span className="checkmark">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12"></polyline>
                                                    </svg>
                                                </span>
                                            )}
                                        </button>
                                        <button 
                                            type="button"
                                            className={`dropdown-item ${widgetFilter === 'pro' ? 'active' : ''}`}
                                            onClick={() => {
                                                setWidgetFilter(widgetFilter === 'pro' ? 'all' : 'pro');
                                                setMenuOpen(false);
                                            }}
                                        >
                                            <span>{__('Pro', 'huge-addons-for-elementor')}</span>
                                            {widgetFilter === 'pro' && (
                                                <span className="checkmark">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12"></polyline>
                                                    </svg>
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ): (
                        <div 
                            className="view-all-widget" 
                            onClick={() => setActiveItem && setActiveItem('widgets')}
                        >
                            {__('View All Widget', 'huge-addons-for-elementor')} <span className="thha-icon thha-icon-externel-link w16" style={{ width: '16px', height: '16px' }}></span>
                        </div>
                    )}
                </div>
            </div>

            {isSearchEnable && (
                <div className="category-filter">
                    {categories.map((category, index) => (
                        <CategoryTab
                            key={index}
                            name={category.name}
                            count={category.count}
                            active={category.name === activeCategory}
                            onClick={setActiveCategory}
                        />
                    ))}
                </div>
            )}

            {/* Form for saving changes */}
            <form onSubmit={handleSaveChanges}>
                <div className="thha-widget-grid">
                    {Object.entries(filteredWidgets || {}).map(([categoryName, widgetsObjects]) => {
                        // Check if widgetsObject is not empty
                        if (!widgetsObjects || Object.keys(widgetsObjects).length === 0) return null;

                        const sortedWidgetEntries = Object.entries(widgetsObjects)
                            .map((entry, index) => ({ entry, index }))
                            .sort((a, b) => {
                                const aOrder = Number.isFinite(Number(a.entry[1].order)) ? Number(a.entry[1].order) : Number.MAX_SAFE_INTEGER;
                                const bOrder = Number.isFinite(Number(b.entry[1].order)) ? Number(b.entry[1].order) : Number.MAX_SAFE_INTEGER;

                                if (aOrder !== bOrder) {
                                    return aOrder - bOrder;
                                }

                                // Preserve original insertion order when order is the same or missing.
                                return a.index - b.index;
                            })
                            .map((item) => item.entry);

                        return (
                            <div key={categoryName} className="widget-category-group">
                                <h2 className='widget-category-title'>{categoryName.charAt(0).toUpperCase() + categoryName.slice(1)}</h2>
                                <div className="widget-cards-container">
                                    
                                    {sortedWidgetEntries
                                        .slice(0, perCategoryItem === 'all' ? undefined : perCategoryItem)
                                        .map(([widgetClass, widgetData]) => {

                                        const isEnabled = enabledWidgets.includes(widgetClass);
                                        const typeClass = widgetData.is_pro ? 'pro' : 'free';
                                        const isDependencyLocked = !!widgetData.dependency_locked;
                                        const dependencyMessage = widgetData.dependency_message || __('Requires active dependency plugin.', 'huge-addons-for-elementor');
                                         

                                        return (
                                            <div key={widgetClass} className={`thha-widget-card ${typeClass} ${widgetData.status} ${isDependencyLocked ? 'dependency-locked' : ''}`}>
                                                <div className="row">
                                                    {/* Widget Icon */}
                                                    <div className="thha-widget-icon">
                                                        {widgetData.icon.startsWith('eicon-') ? (
                                                            <i className={widgetData.icon}></i>
                                                        ) : (
                                                            <img
                                                                src={`${THHA_URL}${widgetData.icon}`}
                                                                alt={widgetData.name}
                                                                className="thha-widget-icon-image"
                                                            />
                                                        )}
                                                    </div>

                                                    {/* Toggle Switch/Checkbox */}
                                                    { (!widgetData.is_pro || window.thhaAdminSettings.is_pro_active) && widgetData.status !== 'upcoming' ? (
                                                        <>
                                                        {widgetData.is_pro && (
                                                            <div>
                                                                <span className="pro-badge">pro</span>
                                                            </div>
                                                        )}
                                                        <label
                                                            className="thha-toggle-switch"
                                                            onClick={isDependencyLocked ? (e) => handleLockedToggleClick(e, dependencyMessage) : undefined}
                                                            title={isDependencyLocked ? dependencyMessage : ''}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isEnabled}
                                                                disabled={isDependencyLocked}
                                                                title={isDependencyLocked ? dependencyMessage : ''}
                                                                onChange={(e) => handleToggle(widgetClass, e.target.checked)}
                                                            />
                                                            <span className="thha-slider"></span>
                                                        </label>
                                                        </>

                                                    ):(
                                                        <>
                                                        {/* PRO Tag Logic */}
                                                        {widgetData.is_pro && (
                                                            <div>
                                                                <span className="pro-badge">pro</span>
                                                            </div>
                                                        )}
                                                        {widgetData.status === 'upcoming' && !widgetData.is_pro && (
                                                            <div>
                                                                <span className="upcoming-badge">UPCOMING</span>
                                                            </div>
                                                        )}
                                                        </>
                                                    )}

                                                </div>

                                                {/* Widget Info */}
                                                <div className="thha-widget-info row">
                                                    <div className="thha-widget-title">
                                                        {widgetData.name}
                                                    </div>
                                                </div>
                                                <div className="thha-widget-links row">
                                                    <a href={widgetData.doc_link} target="_blank" rel="noopener noreferrer"><span className="thha-icon thha-icon-documentation w18"></span>Documentation</a>
                                                    <a href={widgetData.demo_link} target="_blank" rel="noopener noreferrer"><span className="thha-icon thha-icon-link w18"></span>Demo</a>
                                                </div>
                                                
                                                
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
{/* 
                <p className="save-button-wrapper">
                    <button type="submit" className="button button-primary" disabled={isSaving}>
                        {isSaving ? __('Saving...', 'huge-addons-for-elementor') : __('Save Changes', 'huge-addons-for-elementor')}
                    </button>
                </p> */}
            </form>

        </div>
    );
};

export default Widgets;